/**
 * Which day a reminder belongs on.
 *
 * A snoozed task's WAKE DATE is `snoozed_until`, not `due_date` — snoozing is a
 * promise to resurface it later, so it must leave today's list and appear on the
 * day it wakes. Filtering on `due_date` alone gets this exactly backwards: a
 * task snoozed to tomorrow keeps its original (often already-overdue) due date,
 * so it would stay on today's list forever and never appear on tomorrow's.
 *
 * Dates are compared as `YYYY-MM-DD` STRINGS, deliberately. Both fields are
 * wall-clock at the location, and `dateService.fromAPIDate` already truncates
 * the same way (`substring(0, 10)`), as does `CalendarGrid.toCalendarDate` —
 * parsing to a Date and back shifts the day backwards in HST/PST.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/taskVisibility.test.mjs`. Do not add imports.
 */

/** `YYYY-MM-DD` from a date, datetime, or moment-formatted string. */
export function toCalendarDate(value) {
  if (!value) return '';
  if (typeof value === 'object' && typeof value.format === 'function') {
    return value.format('YYYY-MM-DD');
  }
  return String(value).substring(0, 10);
}

/**
 * The day this task should surface on, as `YYYY-MM-DD`, or null if it has no
 * date at all (those are always actionable and never filtered out).
 */
export function taskWakeDate(task) {
  const t = task || {};
  if (String(t.status || '').trim().toLowerCase() === 'snoozed') {
    const wake = toCalendarDate(t.snoozed_until || t.snoozedUntil);
    if (wake) return wake;
    // Snoozed with no wake date on the payload — fall through to due_date
    // rather than hiding it. A reminder that vanishes is worse than one that
    // reappears early.
  }
  return toCalendarDate(t.due_date || t.dueDate) || null;
}

/**
 * Should this task appear on `selectedDate`?
 *
 * Today is the catch-up view: anything wake-dated today OR EARLIER rolls up, so
 * nothing is lost by not having looked yesterday. Other days show only what
 * wakes on that day — a future view listing every overdue item would be noise,
 * and those items are already on today.
 */
export function isTaskVisibleOn(task, selectedDate, today) {
  const wake = taskWakeDate(task);
  if (!wake) return true;
  const sel = toCalendarDate(selectedDate);
  if (!sel) return true;
  if (sel === toCalendarDate(today)) return wake <= sel;
  return wake === sel;
}

/**
 * Snoozed tasks whose payload carries no wake date.
 *
 * Diagnostic only. If `GET /api/tasks` omits `snoozed_until`, every snoozed
 * task silently falls back to `due_date` and the wake-date behaviour looks
 * simply broken rather than unsupported — this makes the cause visible.
 */
export function snoozedWithoutWakeDate(tasks) {
  if (!Array.isArray(tasks)) return [];
  return tasks.filter(
    (t) =>
      t &&
      String(t.status || '').trim().toLowerCase() === 'snoozed' &&
      !toCalendarDate(t.snoozed_until || t.snoozedUntil)
  );
}
